import axios, { AxiosError, AxiosResponse } from 'axios'
import { store } from '@/store'
import router from '@/router/router.js'
import { _ } from 'core-js'
import events from '@/events/events'
import { randomKey } from '@/utility/helpers'

//  Set the api url
axios.defaults.baseURL = process.env.VUE_APP_TEST_URL || process.env.VUE_APP_URL_API

//  Attach common headers
axios.defaults.headers.common = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
}

axios.defaults.transformRequest = [
    (data, headers) => {
        //  Add the 'Path' header (this is used by Analytics to track app location)
        headers['Path'] = router.currentRoute.path

        //  If data is null, this is a GET or DELETE so just return
        if (!data) return data

        //  Modify the headers to accept json
        headers['Content-Type'] = 'application/json;charset=UTF-8'

        //  Container for the parsed data
        let parse = data

        //  Turn our data into a string
        let json = JSON.stringify(parse)

        //  Try to parse this data back into JSON and if it returns an empty object, just use our default
        json = JSON.parse(json)

        //  If this json object is empty, return with data
        if (_.isEmpty(json)) return data
        //  Else just stringify it again
        else json = JSON.stringify(json)

        //  Parse out all HTML tags from it
        parse = json.replace(/<\/?[^>]+(>|$)/g, "")

        //  If we can't turn this back into a JSON object, we messed up, and use the original
        try {
            parse = JSON.parse(parse)
            //  Iterate through each key value and fix them if blank
            Object.keys(parse).forEach(key => parse[key] = parse[key] === '' ? ' ' : parse[key])
            //  Stringify it back
            parse = JSON.stringify(parse)
        }
        catch (e) { parse = JSON.stringify(data) }

        //  Return the parsed data
        //  If an error still gets thrown at this point, it might potentially be fully user errro
        return parse
    }
]

//  Attach global request interceptor
axios.interceptors.request.use((request) => {
    //  Resolve the request promise
    return Promise.resolve(request)
})

//  Attach global response interceptor
axios.interceptors.response.use(
    (response: AxiosResponse) => {
        return Promise.resolve(response)
    },
    (error: AxiosError) => {
        switch (error.response.status) {
            case 401:
                return error401(error)  //  Authentication error
            case 402:
                return error402(error)  // Payment Error
            case 403:
                return error403(error)  //  Permission or Feature Error
            // case 404:
            //     return error404(error)  // Resource Not found
            case 406:
                return error406(error) //  Permission denied
            case 412:
                return error412(error)  // Text Message Credit Limit
            case 422:
                return error422(error)  // Validation Error
            case 503:
                return error503(error)
            default:
                return Promise.reject(error)
        }
    }
)

//  Bind axios instance to window, so we can use it anywhere
window.axios = axios

export default axios

async function error401(error: AxiosError): Promise<void> {
    //  If we aren't getting the token again, this is a genuine 401, log user out
    store.commit('authTokenSet', null)
    store.commit('authTwoFactorTokenSet', null)
    store.commit('authUserSet', null)

    //  Get the current path from the url
    const path = window.root.$route.path

    //  Create list of safe paths (ie paths with AuthGuardNot)
    const paths = ['/signin', '/qbo/link-account', '/xero/link-account']

    //  If we're not on a safe path, redirect to home
    if (!(paths.includes(path))) router.push('/')

    return Promise.reject(error)
}
function error402(error: AxiosError): Promise<void> {
    const message = errorExtract(error)
    errorDispatch('Payment Error', message)
    return Promise.reject(error)
}
function error403(error: AxiosError): Promise<void> {
    //  Get the error message from the http error
    const message = errorExtract(error)

    //  Check if the 403 is from a suspension
    const isSuspension = message.startsWith('This account has been flagged for abuse');

    //  If the error is a 'suspension', pop special suspension notification
    if (isSuspension) events.onTenantSuspendedHttpResponse(message)

    //  Else, pop generic notification
    else errorDispatch("Access Denied", message)

    //  Reject and return the error
    return Promise.reject(error)
}
function error406(error: AxiosError): Promise<void> {
    return Promise.reject(error)
}
function error412(error: AxiosError): Promise<void> {
    const message = errorExtract(error)
    store.dispatch('dialogClose', 'APP')
    errorDispatch('Text Message Credit Limit', message, '/billing/subscription')
    return Promise.reject(error)
}
function error422(error: AxiosError): Promise<void> {
    //  Get the error message
    const message = errorExtract(error)

    //  Pop the validation error message
    errorDispatch('Validation Error', message, null, false)

    //  Reject the error
    return Promise.reject(error)
}
function error503(error: AxiosError): Promise<void> {
    store.commit('splashSet', {
        loader: false,
        message: `We are currently experiencing some downtime, please be patient while
                  we do our best to get back up and running.<br>
                  Email <a href="mailto:josh@messagesdesk.com">josh@messagesdesk.com</a>
                  with any concerns or questions you may have.`
    })
    return Promise.reject(error)
}
function errorDispatch(title: string, text: string, page: string = null, reload: boolean = true): void {
    //  Log our error
    store.getters.appDebug && console.error("ERROR: ", title, text)

    //  Grab our user from the store
    const user = store.getters.user

    //  If there is no user OR dialog is set to true, then pop a dialog
    if (!user || !user.memberships.length) {
        store.dispatch('dialogOpen', {
            type: 'MESSAGE',
            title: title,
            text: text,
            icon: 'mdi-alert-circle',
            color: 'yellow darken-2',
            callback: () => {
                if (page) router.push(page)
            }
        })
    }
    else {
        //  Else, for our user we will show the notification pop up
        store.commit('notificationPopupOpen', {
            title: title,
            text: text || 'An error occurred, to continue please refresh your page',
            icon: 'bi-emoji-dizzy-fill',
            id: randomKey(),
            reload: reload,
            error: true
        })
    }
}
function errorExtract(error: AxiosError): string {
    let text = ''

    //  Check if the error is a laravel validate error
    if (error.response.data.errors) {
        Object.values(error.response.data.errors).forEach(value => {
            text = value[0]
        })
    }

    //  If the error is a snapdsk validate error
    if (error.response.data.error) {
        text = error.response.data.error
    }

    return text
}
